import Vue from "vue";
import vue from "./main";
import VueRouter from "vue-router";
import routes from "@/routes";
import store from "@/store/store";
import utils from "@/utils";
import { DateTime } from "luxon";

class Router extends VueRouter {
  previousRoute = null;
  activeRoute = null;

  push(data) {
    super.push(data).catch(() => {
    });
  }

  changeCompany(code) {
    if (this.previousRoute !== null)
      this.previousRoute.params["ccode"] = code;
    if (this.activeRoute !== null)
      this.activeRoute.params["ccode"] = code;
  }

  close(noPrevious = null) {
    if (this.previousRoute !== undefined && this.previousRoute !== null && this.previousRoute.fullPath !== "/") {
      this.replace(this.previousRoute);
      this.previousRoute = null;
    } else if (noPrevious === null) {
      this.go(-1);
    } else {
      this.replace(noPrevious);
    }
  }

  routeToMap() {
    if (this.previousRoute !== undefined && this.previousRoute !== null) {
      if (this.previousRoute.fullPath.includes("map-preview"))
        this.replace({ name: "map-preview" });
      else if (this.previousRoute.fullPath.includes("plan")) {
        this.go(-1)
        this.previousRoute = null;
      } else
        this.replace({ name: "map" });
    } else
      this.replace({ name: "map" });
  }

  parseQueryBool(route, name) {
    if (route.query[name] !== undefined)
      return route.query[name] === "true";
    else
      return undefined;
  }

  parseQueryVehicleId(route, name = "vehicleId") {
    if (route.query[name] !== undefined)
      return { vehicleId: parseInt(route.query[name]) };
    else
      return undefined;
  }

  parseQueryVehicleIds(route, name = "vehicleIds") {
    if (route.query[name] !== undefined)
      return route.query[name].toString().split(",").map(x => {
        return { vehicleId: parseInt(x) };
      });
    else
      return undefined;
  }

  parseQueryPersonId(route, name = "personId") {
    if (route.query[name] !== undefined)
      return { personId: parseInt(route.query[name]) };
    else
      return undefined;
  }

  parseQueryPeriod(route, name = "period") {
    if (route.query[name] !== undefined) {
      const p = route.query[name].split("|");
      const now = DateTime.now();

      if (p[0] === "month-current") {
        const date_from = now.startOf("month").toFormat("yyyy-MM-dd");
        const date_to = now.endOf("month").toFormat("yyyy-MM-dd");
        return { name: p[0], from: date_from, to: date_to };
      } else if (p[0] === "month-previous") {
        const date = now.subtract(1, "months");
        const date_from = date.startOf("month").toFormat("yyyy-MM-dd");
        const date_to = date.endOf("month").toFormat("yyyy-MM-dd");
        return { name: p[0], from: date_from, to: date_to };
      } else if (p[0] === "month-one" && p.length > 1) {
        const date_from = DateTime.fromISO(p[1]).startOf("month").toFormat("yyyy-MM-dd");
        const date_to = DateTime.fromISO(p[1]).endOf("month").toFormat("yyyy-MM-dd");
        return { name: p[0], from: date_from, to: date_to };
      } else if (p[0] === "month-range" && p.length > 2) {
        const date_from = DateTime.fromISO(p[1]).startOf("month").toFormat("yyyy-MM-dd");
        const date_to = DateTime.fromISO(p[2]).endOf("month").toFormat("yyyy-MM-dd");
        return { name: p[0], from: date_from, to: date_to };
      } else if (p[0] === "day-current") {
        const date = now.toFormat("yyyy-MM-dd");
        return { name: p[0], from: date, to: date };
      } else if (p[0] === "day-previous") {
        const date = now.subtract(1, "days").toFormat("yyyy-MM-dd");
        return { name: p[0], from: date, to: date };
      } else if (p[0] === "day-one" && p.length > 1) {
        const date = DateTime.fromISO(p[1]).toFormat("yyyy-MM-dd");
        return { name: p[0], from: date, to: date };
      } else if (p[0] === "day-range" && p.length > 2) {
        const date_from = DateTime.fromISO(p[1]).toFormat("yyyy-MM-dd");
        const date_to = DateTime.fromISO(p[2]).toFormat("yyyy-MM-dd");
        return { name: p[0], from: date_from, to: date_to };
      } else if (p[0] === "day-rangetime" && p.length > 2) {
        const date_from = DateTime.fromISO(p[1]).toFormat("yyyy-MM-dd");
        const date_to = DateTime.fromISO(p[2]).toFormat("yyyy-MM-dd");
        const time_from = DateTime.fromISO(p[1]).toFormat("HH:mm");
        const time_to = DateTime.fromISO(p[2]).toFormat("HH:mm");
        return {
          name: p[0],
          from: date_from,
          to: date_to,
          fromTime: time_from,
          toTime: time_to
        };
      }
    }

    return undefined;
  }

  periodToQuery(period) {
    if (utils.isValue(period)) {
      var result = period.name;

      if (period.name === "month-one")
        result += "|" + DateTime.fromISO(period.from).toFormat("yyyy-MM");
      else if (period.name === "month-range")
        result += "|" + DateTime.fromISO(period.from).toFormat("yyyy-MM")
          + "|" + DateTime.fromISO(period.to).toFormat("yyyy-MM");
      else if (period.name === "day-one")
        result += "|" + DateTime.fromISO(period.from).toFormat("yyyy-MM-dd");
      else if (period.name === "day-range")
        result += "|" + DateTime.fromISO(period.from).toFormat("yyyy-MM-dd")
          + "|" + DateTime.fromISO(period.to).toFormat("yyyy-MM-dd");
      else if (period.name === "day-rangetime")
        result += "|" + DateTime.fromISO(period.from).toFormat("yyyy-MM-dd")
          + "T" + ((utils.isValue(period.fromTime)) ? period.fromTime : "00:00")
          + "|" + DateTime.fromISO(period.to).toFormat("yyyy-MM-dd")
          + "T" + ((utils.isValue(period.toTime)) ? period.toTime : "23:59");

      return result;
    } else {
      return undefined;
    }
  }

  checkPermissions(to) {
    let valid = true;

    to.matched.forEach(route => {
      if (route.meta.module !== undefined) {
        if (route.meta.access === "write")
          valid = vue.canWrite(route.meta.module);
        else if (route.meta.access === "delete")
          valid = vue.canDelete(route.meta.module);
        else
          valid = vue.canRead(route.meta.module);
      } else if (route.meta.role !== undefined) {
        if (store.state.auth.token !== null) {
          if (route.meta.role === "superuser" && !store.state.auth.token.superuser)
            valid = false;
          if (route.meta.role === "admin"
            && !store.state.auth.token.superuser
            && (store.state.auth.company === null || !store.state.auth.company.administrator))
            valid = false;
        }
      }
    });

    return valid;
  }
}

const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  routes: routes
});


// eslint-disable-next-line no-unused-vars
router.beforeEach(async (to, from, next) => {
  if (from === to)
    return;

  if (router.activeRoute === null || to.name !== router.activeRoute.name) {
    router.previousRoute = router.activeRoute;
    router.activeRoute = to;
  }

  if (to.params.ccode !== undefined && to.params.ccode !== null &&
    ((store.state.auth.company != null && store.state.auth.company.code !== to.params.ccode)
      || store.state.auth.token === null)) {

    await store.dispatch("auth/setCompany", to.params.ccode);
  }

  // kontrola oprávnění
  let valid = true;

  if (store.state.auth.token !== null) {
    valid = router.checkPermissions(to);
  }

  if (valid)
    next();
  else
    next({ path: `/${to.params.ccode}/map` });
});

Vue.use(Router);

export default router;